import * as Api from '@ViewModels';
import { StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../models';
import { useEventLogging } from '../../../models/Logging';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../components-v2/Select';

interface SelectOption<T = unknown> {
	dataContext: T;
	hoverText?: string;
	id: string;
	styles?: StyleDeclarationValue[];
	text?: string;
}
import { UserSelectModal } from '../UserSelectModal';
import { styleSheet } from './styles';

interface IProps extends IImpersonationContextComponentProps {
	additionalPinnedOptions?: SelectOption<string | Api.UserViewModel>[];
	defaultOption?: SelectOption<string | Api.UserViewModel>;
	disabled?: boolean;
	initialSelectedOption?: SelectOption<string | Api.UserViewModel>;
	includeSearch?: boolean;
	onFilterOptions?(options: SelectOption<string | Api.UserViewModel>[]): SelectOption<string | Api.UserViewModel>[];
	onOptionSelected?(option: SelectOption<string | Api.UserViewModel>, user?: Api.UserViewModel): void;
	onRenderPlaceholder?(): React.ReactNode;
	styles?: StyleDeclarationValue[];
}

const UserSelectBoxBase: React.FC<IProps> = observer(props => {
	const {
		styles = [],
		defaultOption,
		onOptionSelected,
		onFilterOptions,
		includeSearch = true,
		disabled,
		initialSelectedOption,
		additionalPinnedOptions,
		impersonationContext,
	} = props;
	const userSession = useUserSession();
	const [selectedUser, setSelectedUser] = React.useState<Api.UserViewModel>(null);
	const { logApiError } = useEventLogging('UserSelectBox');
	const options = React.useMemo(() => {
		const selectedUserOption = selectedUser
			? {
					dataContext: selectedUser,
					hoverText: selectedUser.email,
					id: `option-${selectedUser.id}`,
					styles: [styleSheet.selectOption],
					text: selectedUser.name,
				}
			: null;
		const searchOption = {
			dataContext: 'search',
			id: 'option-search',
			styles: [styleSheet.selectOption],
			text: 'Search Employee',
		};

		const currentUser = impersonationContext?.user
			? new Api.UserViewModel(userSession, impersonationContext.user).impersonate(impersonationContext)
			: new Api.UserViewModel(userSession, userSession.user);
		const computedOptions: SelectOption<string | Api.UserViewModel>[] = [
			defaultOption ? { ...defaultOption, styles: [styleSheet.selectOption] } : defaultOption,
			selectedUserOption,
			selectedUserOption?.dataContext?.id !== currentUser.id
				? {
						dataContext: currentUser,
						hoverText: currentUser.email,
						id: `option-${currentUser.id}`,
						styles: [styleSheet.selectOption],
						text: userSession.user.id === currentUser.id ? 'Me' : currentUser.name,
					}
				: null,
			...(additionalPinnedOptions || []),
			includeSearch ? searchOption : null,
		].filter(x => !!x);
		return onFilterOptions ? onFilterOptions(computedOptions) : computedOptions;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedUser, defaultOption, onFilterOptions, additionalPinnedOptions]);
	React.useEffect(() => {
		if (typeof initialSelectedOption?.dataContext === 'object') {
			setSelectedUser(initialSelectedOption.dataContext);
		} else if (initialSelectedOption?.dataContext) {
			const user = new Api.UserViewModel(userSession, {
				id: initialSelectedOption.dataContext,
			}).impersonate(impersonationContext);
			user
				.load()
				.then(() => {
					// FOLLOWUP: Resolve

					// @ts-ignore
					setSelectedUser(user);
				})
				.catch(error => {
					logApiError('UserLoad-Error', error);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [impersonationContext?.account, impersonationContext?.user]);

	const [isUserSelectModalOpen, setIsUserSelectModalOpen] = React.useState<boolean>(false);
	const onUserSelectRequestClose = React.useCallback(
		(user?: Api.UserViewModel, cancel?: boolean) => {
			if (!cancel) {
				setSelectedUser(user);
				onOptionSelected?.({
					dataContext: user,
					id: user.id,
					text: user.name,
				});
			}
			setIsUserSelectModalOpen(false);
		},
		[onOptionSelected]
	);
	const userSelectModalProps = React.useMemo(
		() => ({
			isOpen: isUserSelectModalOpen,
			onRequestClose: onUserSelectRequestClose,
		}),
		[isUserSelectModalOpen, onUserSelectRequestClose]
	);

	const selectedOption = selectedUser
		? options.find(x => (x.dataContext as Api.UserViewModel)?.id === selectedUser.id)
		: defaultOption;

	const onValueChange = React.useCallback(
		(id: string) => {
			const opt = options.find(x => x.id === id);
			if (!opt) return;

			if (opt.dataContext === 'search') {
				setIsUserSelectModalOpen(true);
				return;
			}

			if (typeof opt.dataContext === 'string') {
				setSelectedUser(null);
				onOptionSelected?.(opt, typeof defaultOption?.dataContext !== 'string' ? defaultOption?.dataContext : null);
			} else {
				setSelectedUser(opt.dataContext);
				onOptionSelected?.(opt, opt.dataContext);
			}
		},
		[options, defaultOption, onOptionSelected]
	);

	return (
		<>
			<Select value={selectedOption?.id} onValueChange={onValueChange}>
				<SelectTrigger disabled={disabled} styles={[styleSheet.container, ...styles]}>
					{selectedOption?.text ?? props.onRenderPlaceholder?.()}
				</SelectTrigger>
				<SelectContent>
					{options.map(opt => (
						<SelectItem key={opt.id} value={opt.id}>
							{opt.text}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			<UserSelectModal modalProps={userSelectModalProps} title='User Search' />
		</>
	);
});

export const UserSelectBox = inject(ImpersonationContextKey)(UserSelectBoxBase);
